import { createActions, handleActions } from 'redux-actions';
import { destroy as destroyForm } from 'redux-form';
import Notifications from 'react-notification-system-redux';
import {
    defaultPaginationData,
    getPaginationAndOrderData,
} from '../../shared/utils';

const actions = createActions({
    STATIONS: {
        LIST: {
            FILTER: filter => ({ filter }),
            FETCHING: currentPage => ({ currentPage }),
            LOADED: list => ({ list }),
        },
        EDIT: {
            FETCHING: stationId => ({ stationId }),
            LOADED: station => ({ station }),
        },
        UPDATE: {
            UPDATING: undefined,
            UPDATED: station => ({ station }),
        },
        ADD: {
            ADDING: undefined,
            ADDED: station => ({ station }),
        },
        REMOVE: {
            PROMPT: ({ stationId, stationName }) => ({
                stationId,
                stationName,
            }),
            REMOVING: undefined,
            REMOVED: undefined,
            RESET: undefined,
        },
    },
});

const initialState = Object.assign(
    {},
    {
        list: Object.assign(
            {},
            {
                filter: null,
                error: null,
                fetching: false,
                fetchingTime: null,
                stations: null,
                currentPage: 1,
            },
            defaultPaginationData
        ),
        edit: {
            error: null,
            fetching: false,
            station: null,
        },
        update: {
            error: null,
            updating: false,
        },
        add: {
            error: null,
            adding: false,
        },
        remove: {
            stationId: null,
            stationName: null,
            removing: false,
        },
    }
);

let fetchingTimer;

export default handleActions(
    {
        [actions.stations.list.filter]: (state, action) => {
            return Object.assign({}, state, {
                list: Object.assign({}, state.list, {
                    filter: action.payload.filter,
                }),
            });
        },

        [actions.stations.list.fetching]: (state, action) => {
            fetchingTimer = Date.now();

            return Object.assign({}, state, {
                list: Object.assign({}, state.list, {
                    fetching: true,
                    currentPage: action.payload.currentPage,
                }),
            });
        },

        [actions.stations.list.loaded]: {
            next: (state, action) => {
                fetchingTimer = Date.now() - fetchingTimer;

                return Object.assign({}, state, {
                    list: Object.assign(
                        {},
                        state.list,
                        getPaginationAndOrderData(action.payload.list.meta),
                        {
                            stations: action.payload.list.results.reduce(
                                (result, station) => {
                                    result[station.stationId] = {
                                        stationId: station.stationId,
                                        stationName: station.stationName,
                                        stationCode: station.stationCode,
                                        stationLat: station.stationLat,
                                        stationLong: station.stationLong,
                                    };
                                    return result;
                                },
                                {}
                            ),
                            fetching: false,
                            fetchingTime: fetchingTimer,
                        }
                    ),
                });
            },
            throw: (state, action) => {
                return Object.assign({}, state, {
                    list: Object.assign({}, initialState.list, {
                        error: true,
                    }),
                });
            },
        },

        [actions.stations.edit.fetching]: (state, action) => {
            return Object.assign({}, state, {
                edit: Object.assign({}, initialState.edit, {
                    fetching: true,
                }),
            });
        },

        [actions.stations.edit.loaded]: {
            next: (state, action) => {
                return Object.assign({}, state, {
                    edit: Object.assign({}, initialState.edit, {
                        station: Object.assign({}, action.payload.station),
                    }),
                });
            },
            throw: (state, action) => {
                return Object.assign({}, state, {
                    list: Object.assign({}, initialState.edit, {
                        error: true,
                    }),
                });
            },
        },

        [actions.stations.update.updating]: (state, action) => {
            return Object.assign({}, state, {
                update: Object.assign({}, initialState.update, {
                    updating: true,
                }),
            });
        },

        [actions.stations.update.updated]: {
            next: (state, action) => {
                return Object.assign({}, state, {
                    edit: Object.assign({}, initialState.edit, {
                        station: action.payload.station,
                    }),
                    update: Object.assign({}, initialState.update),
                });
            },
            throw: (state, action) => {
                return Object.assign({}, state, {
                    update: Object.assign({}, initialState.update, {
                        error: true,
                    }),
                });
            },
        },

        [actions.stations.add.adding]: (state, action) => {
            return Object.assign({}, state, {
                update: Object.assign({}, initialState.add, {
                    updating: true,
                }),
            });
        },

        [actions.stations.add.added]: {
            next: (state, action) => {
                return Object.assign({}, state, {
                    add: Object.assign({}, initialState.add, {
                        station: action.payload.station,
                    }),
                    update: Object.assign({}, initialState.add),
                });
            },
            throw: (state, action) => {
                return Object.assign({}, state, {
                    update: Object.assign({}, initialState.add, {
                        error: true,
                    }),
                });
            },
        },

        [actions.stations.remove.prompt]: (state, action) => {
            const { stationId, stationName } = action.payload;

            return Object.assign({}, state, {
                remove: Object.assign({}, initialState.remove, {
                    stationId,
                    stationName,
                }),
            });
        },

        [actions.stations.remove.removing]: (state, action) => {
            return Object.assign({}, state, {
                remove: Object.assign({}, state.remove, {
                    removing: true,
                }),
            });
        },

        [actions.stations.remove.removed]: (state, action) => {
            return Object.assign({}, state, {
                remove: Object.assign({}, initialState.remove),
            });
        },

        [actions.stations.remove.reset]: (state, action) => {
            return Object.assign({}, state, {
                remove: Object.assign({}, initialState.remove),
            });
        },
    },
    initialState
);

export const filterStations = filter => {
    return actions.stations.list.filter(filter);
};

export const loadStations = (currentPage, limit = 20) => {
    return (dispatch: Function, getState: Function, api: Function) => {
        const filter = getState().stations.list.filter || '';

        dispatch(actions.stations.list.fetching({ currentPage }));

        return api
            .get(
                `/stations?limit=${limit}&page=${currentPage}&filter=${filter}`
            )
            .then(
                response =>
                    dispatch(actions.stations.list.loaded(response.data)),
                err => dispatch(actions.stations.list.loaded(err))
            );
    };
};

export const loadStationDetails = stationId => {
    return (dispatch: Function, getState: Function, api: Function) => {
        dispatch(actions.stations.edit.fetching());

        return api
            .get(`/stations/${stationId}`)
            .then(
                response =>
                    dispatch(actions.stations.edit.loaded(response.data)),
                err => dispatch(actions.stations.edit.loaded(err))
            );
    };
};

export const updateStationDetails = (stationId, stationData) => {
    return (dispatch: Function, getState: Function, api: Function) => {
        dispatch(actions.stations.update.updating());

        return api.put(`/stations/${stationId}`, stationData).then(
            response => {
                dispatch(actions.stations.update.updated(response.data));

                dispatch(
                    Notifications.success({
                        title: 'Station updated',
                        message: 'The provided details have been saved.',
                        position: 'tc',
                        autoDismiss: 3,
                    })
                );
            },
            err => {
                dispatch(actions.stations.update.updated(err));
                dispatch(
                    Notifications.error({
                        title: 'Update failed',
                        message:
                            'An error occurred whilst saving this station. Please try again.',
                        position: 'tc',
                        autoDismiss: 5,
                    })
                );
                throw err;
            }
        );
    };
};

export const addStationDetails = stationData => {
    return (dispatch: Function, getState: Function, api: Function) => {
        dispatch(actions.stations.add.adding());

        return api.post(`/stations`, stationData).then(
            response => {
                dispatch(actions.stations.add.added(response.data));

                // this is needed to clear the 'dirty' flag and prevent warning that data may be lost
                dispatch(destroyForm('stationAddForm'));

                dispatch(
                    Notifications.success({
                        title: 'Station created',
                        message: 'The provided details have been saved.',
                        position: 'tc',
                        autoDismiss: 3,
                    })
                );
            },
            err => {
                dispatch(actions.stations.add.added(err));
                dispatch(
                    Notifications.error({
                        title: 'Creation failed',
                        message:
                            'An error occurred whilst creating this station. Please try again.',
                        position: 'tc',
                        autoDismiss: 5,
                    })
                );
                throw err;
            }
        );
    };
};

export const showDeleteStationPrompt = (
    stationId: string,
    stationName: string
) => {
    return actions.stations.remove.prompt({ stationId, stationName });
};

export const deletePromptReset = () => {
    return actions.stations.remove.reset();
};

export const removeStation = (stationId: string, stationName: string) => {
    return (dispatch: Function, getState: Function, api: Function) => {
        dispatch(actions.stations.remove.removing());

        return api.delete(`/stations/${stationId}`).then(
            response => {
                dispatch(actions.stations.remove.removed());

                dispatch(
                    Notifications.success({
                        title: 'Station deleted',
                        message: `The station ${stationName} has been deleted.`,
                        position: 'tc',
                        autoDismiss: 3,
                    })
                );
            },
            err => {
                dispatch(actions.stations.remove.removed(err));
                dispatch(
                    Notifications.error({
                        title: 'Delete failed',
                        message:
                            'An error occurred whilst deleting this station. Please try again.',
                        position: 'tc',
                        autoDismiss: 5,
                    })
                );
                throw err;
            }
        );
    };
};



// WEBPACK FOOTER //
// ./src/redux/reducers/stations.js